import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Printer, Download, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  P: 'text-slate-900', WO: 'text-slate-400',
  HO: 'text-indigo-600', '–': 'text-slate-300',
  LWP: 'text-red-700 font-semibold',
};

// Colour a status cell. Combined cells (e.g. ½P½CL) and plain leave-type codes
// (CL, ML, DL, CO …) are amber; LWP red; present slate.
const cellColor = (v) => {
  if (!v) return '';
  if (STATUS_COLOR[v]) return STATUS_COLOR[v];
  if (v.includes('LWP')) return 'text-red-700 font-semibold';
  if (v.replace(/½|P/g, '') !== '') return 'text-amber-600';
  return 'text-slate-900';
};

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

export const MonthlyStatusReport = ({ entityId }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [department, setDepartment] = useState('all');
  const [category, setCategory] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useCallback((extra = {}) => {
    const p = new URLSearchParams({ month, ...extra });
    if (entityId) p.append('entity_id', entityId);
    if (department !== 'all') p.append('department', department);
    if (category !== 'all') p.append('category', category);
    return p;
  }, [month, entityId, department, category]);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_URL}/api/departments`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setDepartments(Array.from(new Set((d || []).map(x => x.name).filter(Boolean))).sort());
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/attendance/status-report?${params()}`, { headers: headers(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to build the report');
      setData(d);
      if (!(d.departments || []).length) toast.info('No staff found for this filter');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const downloadExcel = async () => {
    try {
      const res = await fetch(`${API_URL}/api/attendance/status-report?${params({ format: 'excel' })}`,
        { headers: headers(), credentials: 'include' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `status_report_${month}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Status report downloaded');
    } catch (e) { toast.error(e.message); }
  };

  const print = () => {
    const el = document.getElementById('status-report-print');
    if (!el) { toast.error('Load the report first'); return; }
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print'); return; }
    w.document.write(`<html><head><title>Monthly Status Report</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;margin:12px;}
        table{border-collapse:collapse;width:100%;}
        td,th{border:1px solid #444;padding:1px 2px;font-size:8px;text-align:center;white-space:nowrap;}
        .lbl{text-align:left;font-weight:bold;width:60px;}
        .emp{border:none;text-align:left;font-weight:bold;font-size:10px;padding-top:8px;}
        .dept{border:none;text-align:left;font-weight:bold;font-size:11px;padding-top:10px;}
        h2{text-align:center;margin:2px 0;font-size:14px;}
        .sub{text-align:center;font-size:11px;margin-bottom:8px;}
        .meta{display:flex;justify-content:space-between;font-size:11px;font-weight:bold;margin-bottom:6px;}
        @page{size:A4 landscape;margin:8mm;}
      </style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-4" data-testid="monthly-status-report">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LayoutGrid className="w-5 h-5 text-[#DC2626]" />
            Monthly Status Report (Basic Work Duration)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="sr-month" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Department</Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger data-testid="sr-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Staff Type</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="sr-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  <SelectItem value="Teaching">Teaching</SelectItem>
                  <SelectItem value="Non-Teaching">Non-Teaching</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={load} disabled={loading} className="flex-1" data-testid="sr-load">
                {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Show Report
              </Button>
              <Button variant="outline" onClick={downloadExcel} data-testid="sr-excel">
                <Download className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={print} data-testid="sr-print">
                <Printer className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {data && (
            <p className="mt-3 text-xs text-slate-500" data-testid="sr-legend">
              {Object.entries(data.legend || {}).map(([k, v]) => `${k} = ${v}`).join(' · ')}
            </p>
          )}
        </CardContent>
      </Card>

      {data && (data.departments || []).length > 0 && (
        <Card>
          <CardContent className="overflow-x-auto p-3">
            <div id="status-report-print">
              <style>{`
                #status-report-print table { border-collapse: collapse; width: 100%; }
                #status-report-print td, #status-report-print th {
                  border: 1px solid #94a3b8; padding: 1px 3px; font-size: 10px;
                  text-align: center; white-space: nowrap;
                }
                #status-report-print td.lbl, #status-report-print th.lbl {
                  text-align: left; font-weight: 600; width: 64px;
                }
                #status-report-print td.emp { border: none; text-align: left; font-weight: 600; font-size: 11px; padding-top: 8px; }
                #status-report-print td.dept { border: none; text-align: left; font-weight: 700; font-size: 12px; padding-top: 10px; }
                #status-report-print h2 { text-align: center; font-weight: 700; font-size: 15px; margin: 2px 0; }
              `}</style>
              <h2>Monthly Status Report (Basic Work Duration)</h2>
              <p className="sub text-center text-xs text-slate-500">{data.from} &nbsp;To&nbsp; {data.to}</p>
              <div className="meta flex justify-between text-xs font-semibold my-2">
                <span>Company: {data.company}</span>
                <span>Printed On : {data.printed_on}</span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th className="lbl">Days</th>
                    {(data.days || []).map(d => <th key={d.day}>{d.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.departments.map(dept => (
                    <React.Fragment key={dept.name}>
                      <tr><td className="dept" colSpan={(data.days || []).length + 1}>Department: {dept.name}</td></tr>
                      {dept.employees.map(emp => (
                        <React.Fragment key={emp.employee_id}>
                          <tr data-testid={`sr-emp-${emp.employee_id}`}>
                            <td className="emp" colSpan={(data.days || []).length + 1}>
                              Emp. Code: {emp.emp_code} &nbsp;&nbsp;&nbsp; Emp. Name: {emp.name}
                              &nbsp;&nbsp;&nbsp;<span style={{ fontWeight: 400 }} data-testid={`sr-summary-${emp.employee_id}`}>P {emp.summary.present}{Object.entries(emp.summary.leave_types || {}).map(([k, v]) => ` · L(${k}) ${v}`).join('')} · LWP {emp.summary.lwp || 0} · WO {emp.summary.week_off} · H {emp.summary.holiday} · TOT {emp.summary.total_days} · <b>PAY {emp.summary.payable_days}</b> · Dur {emp.summary.total_hours}</span>
                            </td>
                          </tr>
                          {[['Status', 'status'], ['InTime', 'in'], ['OutTime', 'out'], ['Total', 'total']].map(([label, key]) => (
                            <tr key={key}>
                              <td className="lbl">{label}</td>
                              {emp.rows[key].map((v, i) => (
                                <td key={i} className={key === 'status' ? cellColor(v) : ''}>{v || ''}</td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MonthlyStatusReport;
